import React, { useEffect, useState } from 'react';
import { Modal, Space } from 'antd';
import ReviewForm from './Review';
import EmployeesReviewApi from '../../Api/EmployeesReviewApi';
import { toastError, toastSuccess } from '../Toast/ToastComponent';
import SkillMatrixApi from '../../Api/SkillMatrixApi';

const LOCKED_STATUSES = ['hr review', 'completed'];

function confirmIfAlreadyReviewed(status, action) {
  const normalized = String(status || '').toLowerCase();
  if (!LOCKED_STATUSES.includes(normalized)) {
    return Promise.resolve(true);
  }

  const stageLabel = normalized === 'completed' ? 'Completed' : 'HR Review';
  const actionLabel = action === 'draft' ? 'save a new draft' : 'submit again';

  return new Promise((resolve) => {
    Modal.confirm({
      title: 'Review already submitted',
      content: `This review has already moved to "${stageLabel}" and may have been reviewed by HR. Do you still want to ${actionLabel}? Your changes will overwrite the existing review.`,
      okText: 'Yes, continue',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
}

const feedbackFields = [
  {
    label: 'Key Achievements This Month',
    placeholder: 'Describe your key achievements and accomplishments...',
  },
  {
    label: 'Challenges Faced',
    placeholder: 'Share any challenges or obstacles you encountered...',
  },
  {
    label: 'Suggestions / Improvements',
    placeholder:
      "Provide suggestions for improvement or areas you'd like to develop...",
  },
];

/**
 * Transform form values to API payload format
 * @param {object} formValues - { employee_review: { rates: [...], feedback: [...] } }
 * @param {object} params - { month, year, userId, status }
 */
function buildApiPayload(
  formValues,
  { month, year, userId, status, user_role_id, skillRating },
) {
  const rating = formValues?.employee_review?.rating || [];
  const feedback = formValues?.employee_review?.feedback || [];

  const ratingArray = skillRating.map((row, index) => ({
    id: row.id,
    value: rating[index]?.value ?? 0,
  }));

  const feedbackArray = feedbackFields.map((field, index) => ({
    question: field.label,
    answer: feedback[index]?.answer ?? '',
  }));

  const payload = {
    month,
    year,
    user_id: userId,
    employee_review: {
      rating: ratingArray,
      feedback: feedbackArray,
    },
    user_role_id: user_role_id,
  };

  if (status) {
    payload.status = status;
  }

  return payload;
}

const SelfReviewMonthForm = ({ monthLabel, readOnly, month, year, userId }) => {
  const [saving, setSaving] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const [skillRating, setSkillRating] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      const review = await fetchReviewData({
        month,
        year,
        userId,
        type: 'SELF_REVIEW',
      });
      const skillRating = await fetchSkillRating();
      if (review) {
        setReviewData({
          ...review,
          employee_review: {
            ...review?.employee_review,
            rating: review?.employee_review_populated,
          },
        });
      }
      if (skillRating) {
        setSkillRating(skillRating);
      }
    };
    fetchData();
    return () => {
      setReviewData(null);
      setSkillRating(null);
    };
  }, [month, year, userId]);

  async function fetchSkillRating() {
    try {
      const response = await SkillMatrixApi.getList({});
      return response?.data?.result || null;
    } catch (error) {
      console.error('Error fetching skill rating:', error);
      toastError(
        error?.response?.data?.message || 'Failed to fetch skill rating.',
      );
    }
  }

  async function fetchReviewData({ month, year, userId, type }) {
    try {
      const response = await EmployeesReviewApi.getByDate({
        month,
        year,
        userId,
        type,
      });
      return response?.data?.result || null;
    } catch (error) {
      console.error('Error fetching review data:', error);
      toastError(
        error?.response?.data?.message || 'Failed to fetch review data.',
      );
    }
  }
  const handleSubmit = async (values) => {
    const status =
      reviewData?.status === 'Draft'
        ? reviewData?.previous_status
        : reviewData?.status;
    const proceed = await confirmIfAlreadyReviewed(status, 'submit');
    if (!proceed) return;

    try {
      setSaving(true);
      const payload = buildApiPayload(values, {
        month,
        year,
        userId,
        skillRating,
        status: 'Manager Review',
      });
      await EmployeesReviewApi.create(payload);
      toastSuccess('Review submitted successfully.');
    } catch (error) {
      console.error('Error submitting review:', error);
      toastError(error?.response?.data?.message || 'Failed to submit review.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async (values) => {
    const status =
      reviewData?.status === 'Draft'
        ? reviewData?.previous_status
        : reviewData?.status;

    const proceed = await confirmIfAlreadyReviewed(status, 'draft');
    if (!proceed) return;

    try {
      setSaving(true);
      const payload = buildApiPayload(values, {
        month,
        year,
        userId,
        user_role_id: 3,
        status: 'Draft',
        skillRating,
      });
      await EmployeesReviewApi.create(payload);
      toastSuccess('Draft saved successfully.');
    } catch (error) {
      console.error('Error saving draft:', error);
      toastError(error?.response?.data?.message || 'Failed to save draft.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ReviewForm
      monthLabel={monthLabel}
      readOnly={readOnly || saving}
      onSubmit={handleSubmit}
      onSaveDraft={handleSaveDraft}
      value={reviewData}
    >
      <Space direction="vertical" size="middle" className="w-100">
        <ReviewForm.TimesheetSummary
          summary={{
            totalHours: reviewData?.total_hours || 0,
            projects: reviewData?.projects_worked || 0,
            leaves: reviewData?.total_leaves || 0,
            attendancePct: reviewData?.attendance_percentage || 0,
          }}
        />
        <ReviewForm.PerformanceRating
          title="Self Performance Rating"
          subtitle="Rate yourself on a scale of 1-5 stars"
          rows={reviewData?.employee_review_populated || skillRating}
          parentKey={['employee_review', 'rating']}
        />
        <ReviewForm.Feedback
          title="Self Feedback"
          subtitle="Share achievements, challenges, and improvements for this review period."
          fields={feedbackFields}
          parentKey={['employee_review', 'feedback']}
        />

        <ReviewForm.Actions />
      </Space>
    </ReviewForm>
  );
};

export default SelfReviewMonthForm;
